import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';
import { getWibDateString } from '../utils/presensiLogic';

export interface PresensiRecord {
    id: string;
    user_id: string;
    nama: string;
    tahun_ajaran: string;
    semester: string;

    // Masuk
    shift_masuk: string;
    jam_masuk: string; // ISO
    tanggal?: string;  // YYYY-MM-DD (zona WIB) — dipakai cek duplikat antar-hari
    lokasi_masuk: string;
    koordinat_masuk: string;
    status_masuk: 'tepat_waktu' | 'terlambat';
    alasan_masuk?: string;
    jarak_masuk: number;

    // Pulang
    jam_pulang?: string; // ISO
    lokasi_pulang?: string;
    koordinat_pulang?: string;
    alasan_pulang?: string;
    status_pulang?: 'pulang_cepat' | 'tepat_waktu' | 'lembur' | 'tidak_presensi_pulang';
    jarak_pulang?: number;

    durasi_detik?: number;
}

export const usePresensiPegawai = () => {
    const { authUser } = useAuth();

    const [dailyRecords, setDailyRecords] = useState<PresensiRecord[]>([]);
    const [activeSession, setActiveSession] = useState<PresensiRecord | null>(null);
    const [autoClosedSessionInfo, setAutoClosedSessionInfo] = useState<{ id: string; shiftName: string } | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Guard supaya refetch yang dipicu submit tidak menimpa state pada saat
    // yang sama dengan refetch lain (mis. event `presensi_updated`).
    const fetchSeqRef = useRef(0);

    const userId = authUser?.uid;

    const fetchTodayAttendance = useCallback(async () => {
        if (!userId) return;

        // Abaikan hasil fetch yang sudah kedaluwarsa (ada fetch yang lebih baru).
        const seq = ++fetchSeqRef.current;
        
        try {
            const now = new Date();

            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
            const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

            // Bounded search for unclosed active session (last 3 days)
            const lookbackLimit = new Date(now.getTime() - (3 * 24 * 60 * 60 * 1000));

            const startISO = startOfDay.toISOString();
            const endISO = endOfDay.toISOString();
            const lookbackISO = lookbackLimit.toISOString();

            const { data, error } = await supabase
                .from('presensi_pegawai')
                .select('*')
                .eq('user_id', userId)
                .or(`and(jam_masuk.gte.${startISO},jam_masuk.lte.${endISO}),and(jam_pulang.is.null,jam_masuk.gte.${lookbackISO})`)
                .order('jam_masuk', { ascending: true });

            if (error) throw error;

            const records = (data as PresensiRecord[]) || [];

            // Temukan sesi aktif (sesi terakhir yang belum ditutup dalam jendela lookback)
            let active = records.slice().reverse().find(r => !r.jam_pulang) || null;

            // Retrieve list of already acknowledged auto-closed record IDs from localStorage to prevent duplicate modal popups
            let acknowledgedIds: string[] = [];
            try {
                const stored = localStorage.getItem('ack_auto_closed_presensi');
                if (stored) acknowledgedIds = JSON.parse(stored);
            } catch (e) {
                console.warn('Error reading ack_auto_closed_presensi from localStorage:', e);
            }

            // Client-side auto-close verification for unclosed session
            if (active) {
                const jamMasuk = new Date(active.jam_masuk);
                const diffHours = (now.getTime() - jamMasuk.getTime()) / (1000 * 60 * 60);

                // Auto-close HANYA untuk sesi dari HARI WIB SEBELUMNYA dan sudah
                // ≥ 12 jam. Sesi shift yang aktif HARI INI TIDAK PERNAH ditutup
                // oleh client — penutupan shift aktif hari ini adalah wewenang
                // fungsi server (autoCloseUnclosedPresensi) yang mengevaluasi
                // batas shift+lembur secara dinamis.
                const isPreviousWibDay = getWibDateString(jamMasuk) !== getWibDateString(now);

                if (diffHours >= 12 && isPreviousWibDay) {
                    try {
                        const targetActive = active;
                        const fallbackEnd = new Date(jamMasuk.getTime() + 8 * 60 * 60 * 1000);
                        const pulangTime = jamMasuk > fallbackEnd ? jamMasuk : fallbackEnd;
                        const durationSeconds = Math.max(0, Math.floor((pulangTime.getTime() - jamMasuk.getTime()) / 1000));

                        const { error: patchErr } = await supabase
                            .from('presensi_pegawai')
                            .update({
                                jam_pulang: pulangTime.toISOString(),
                                status_pulang: 'tidak_presensi_pulang',
                                alasan_pulang: 'Ditutup otomatis oleh sistem (tidak presensi pulang)',
                                durasi_detik: durationSeconds,
                                updated_at: now.toISOString()
                            })
                            .eq('id', targetActive.id)
                            .is('jam_pulang', null); // Guard: jangan timpa record yang sudah ditutup

                        if (!patchErr) {
                            setAutoClosedSessionInfo({
                                id: targetActive.id,
                                shiftName: targetActive.shift_masuk || 'Shift'
                            });
                            // Update local records correctly before clearing active
                            const updatedRecords = records.map(r => r.id === targetActive.id ? {
                                ...r,
                                jam_pulang: pulangTime.toISOString(),
                                status_pulang: 'tidak_presensi_pulang' as const,
                                alasan_pulang: 'Ditutup otomatis oleh sistem (tidak presensi pulang)',
                                durasi_detik: durationSeconds
                            } : r);
                            // Refetch berikutnya akan memuat data final dari server;
                            // update lokal di sini cukup untuk konsistensi state sesaat.
                            setDailyRecords(updatedRecords);
                            active = null;
                        }
                    } catch (closeErr) {
                        console.error('Error auto-closing expired session on client:', closeErr);
                    }
                }
            }

            // Check if server Cloud Function (or previous run) auto-closed a recent session that hasn't been acknowledged yet
            if (!active) {
                const unacknowledgedServerClosed = records.slice().reverse().find(r =>
                    r.status_pulang === 'tidak_presensi_pulang' &&
                    r.alasan_pulang &&
                    r.alasan_pulang.includes('Ditutup otomatis') &&
                    !acknowledgedIds.includes(r.id)
                );

                if (unacknowledgedServerClosed) {
                    setAutoClosedSessionInfo({
                        id: unacknowledgedServerClosed.id,
                        shiftName: unacknowledgedServerClosed.shift_masuk || 'Shift'
                    });
                }
            }

            if (seq !== fetchSeqRef.current) return;
            setDailyRecords(records);
            setActiveSession(active);
            setError(null);

        } catch (err: any) {
            console.error('Error fetching attendance:', err);
            // Retain existing dailyRecords & activeSession on network errors
            if (seq === fetchSeqRef.current) {
                setError(err.message);
            }
        } finally {
            if (seq === fetchSeqRef.current) {
                setLoading(false);
            }
        }
    }, [userId]);

    useEffect(() => {
        fetchTodayAttendance();

        const handlePresensiUpdated = () => {
            console.log('Local presensi update event received');
            fetchTodayAttendance();
        };

        window.addEventListener('presensi_updated', handlePresensiUpdated);

        return () => {
            window.removeEventListener('presensi_updated', handlePresensiUpdated);
        };
    }, [fetchTodayAttendance]);

    const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
        const R = 6371e3;
        const φ1 = lat1 * Math.PI/180;
        const φ2 = lat2 * Math.PI/180;
        const Δφ = (lat2-lat1) * Math.PI/180;
        const Δλ = (lon2-lon1) * Math.PI/180;
        const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
                  Math.cos(φ1) * Math.cos(φ2) *
                  Math.sin(Δλ/2) * Math.sin(Δλ/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    };

    const isPointInPolygon = (point: { lat: number, lng: number }, vs: { lat: number, lng: number }[]) => {
        const x = point.lng, y = point.lat;
        let inside = false;
        for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
            const xi = vs[i].lng, yi = vs[i].lat;
            const xj = vs[j].lng, yj = vs[j].lat;
            const intersect = ((yi > y) != (yj > y))
                && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }
        return inside;
    };


    const clearAutoClosedSessionInfo = useCallback(() => {
        if (autoClosedSessionInfo?.id) {
            try {
                const stored = localStorage.getItem('ack_auto_closed_presensi');
                let acknowledgedIds: string[] = stored ? JSON.parse(stored) : [];
                if (!acknowledgedIds.includes(autoClosedSessionInfo.id)) {
                    acknowledgedIds.push(autoClosedSessionInfo.id);
                    localStorage.setItem('ack_auto_closed_presensi', JSON.stringify(acknowledgedIds));
                }
            } catch (e) {
                console.warn('Error saving ack_auto_closed_presensi to localStorage:', e);
            }
        }
        setAutoClosedSessionInfo(null);
    }, [autoClosedSessionInfo]);

    return {
        dailyRecords,
        activeSession,
        autoClosedSessionInfo,
        clearAutoClosedSessionInfo,
        loading,
        error,
        fetchTodayAttendance,
        calculateDistance,
        isPointInPolygon,
    };
};
